import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { store } from '@/store';
import { AuthInit } from '@/contexts/AuthContext';
import { I18nProvider } from '@/contexts/I18nContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import RootRedirect from '@/components/RootRedirect';

// Layouts
import App from '@/App';
import PromoLayout from '@/components/promo/PromoLayout';

// Promo (public) pages
import InfluencersPage from '@/pages/promo/Influencers';
import AdvertisersPage from '@/pages/promo/Advertisers';
import PartnersPage from '@/pages/promo/Partners';
import BlogPage from '@/pages/promo/Blog';
import NewsPage from '@/pages/promo/News';
import ContactPage from '@/pages/promo/Contact';
import InfluencerTermsPage from '@/pages/promo/terms/InfluencerTerms';
import AdvertiserTermsPage from '@/pages/promo/terms/AdvertiserTerms';
import PrivacyPage from '@/pages/promo/terms/Privacy';

// Dashboard (authenticated) pages
import Login from '@/pages/Login';
import Campaigns from '@/pages/Campaigns';
import MyCampaigns from '@/pages/MyCampaigns';
import Profile from '@/pages/Profile';
import CampaignChecklist from '@/pages/CampaignChecklist';
import GrantAnalyticsGuide from '@/pages/GrantAnalyticsGuide';
import GrantYoutubePermissionGuide from '@/pages/GrantYoutubePermissionGuide';

import { updateFavicon } from '@/lib/utils/favicon';
import { detectRuntimeEnvFromBrowser, detectBeTargetEnvFromBrowser } from '@/lib/config/runtimeEnv';

import '@/lib/i18n';
import '@/index.css';

// 환경별 파비콘·타이틀 설정
updateFavicon();
const runtimeEnv = detectRuntimeEnvFromBrowser();
const beTargetEnv = detectBeTargetEnvFromBrowser();
document.documentElement.dataset.env = runtimeEnv;
document.documentElement.dataset.beEnv = beTargetEnv;

if (runtimeEnv === 'local') {
  document.title = `[v2] Influencer - GGContent (local, ${beTargetEnv})`;
} else if (runtimeEnv !== 'live') {
  document.title = `[v2] Influencer - GGContent (${runtimeEnv})`;
}

function Auth({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <I18nProvider>
      <AuthInit>
        <BrowserRouter>
          <Routes>
            {/* === 루트: 로그인 여부에 따라 리다이렉트 === */}
            <Route index element={<RootRedirect />} />

            {/* === 프로모(공개) 페이지 - PromoHeader + PromoFooter === */}
            <Route element={<PromoLayout />}>
              <Route path="influencers" element={<InfluencersPage />} />
              <Route path="advertisers" element={<AdvertisersPage />} />
              <Route path="partners" element={<PartnersPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="blog/:slug" element={<BlogPage />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="news/:language" element={<NewsPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="terms_and_conditions/influencer" element={<InfluencerTermsPage />} />
              <Route path="terms_and_conditions/advertiser" element={<AdvertiserTermsPage />} />
              <Route path="terms_and_conditions/privacy" element={<PrivacyPage />} />
            </Route>

            {/* === 대시보드(인증) 페이지 - Dashboard Header === */}
            <Route element={<App />}>
              <Route path="login" element={<Login />} />

              <Route path="campaign-checklist" element={<CampaignChecklist />} />
              <Route path="campaigns" element={<Auth><Campaigns /></Auth>} />
              <Route path="my-campaigns" element={<Auth><MyCampaigns /></Auth>} />
              <Route path="my-campaigns/:campaignId" element={<Auth><MyCampaigns /></Auth>} />
              <Route path="profile" element={<Auth><Profile /></Auth>} />
              <Route path="guide/analytics" element={<Auth><GrantAnalyticsGuide /></Auth>} />
              <Route path="guide/youtube" element={<Auth><GrantYoutubePermissionGuide /></Auth>} />
            </Route>

            {/* === /bridge/* 원본 경로 호환 (gc-zoku 원본 라우팅) === */}
            <Route path="bridge" element={<App />}>
              <Route index element={<Navigate to="/my-campaigns" replace />} />
              <Route path="login" element={<Login />} />
              <Route path="campaigns" element={<Auth><Campaigns /></Auth>} />
              <Route path="my_campaigns" element={<Auth><MyCampaigns /></Auth>} />
              <Route path="my_campaigns/:campaignId" element={<Auth><MyCampaigns /></Auth>} />
              <Route path="profile" element={<Auth><Profile /></Auth>} />
              <Route path="grant_analytics_guide" element={<Auth><GrantAnalyticsGuide /></Auth>} />
              <Route path="grant_youtube_permission_guide" element={<Auth><GrantYoutubePermissionGuide /></Auth>} />
            </Route>

            {/* === 원본 호환 리다이렉트 === */}
            <Route path="campaign_checklist" element={<Navigate to="/campaign-checklist" replace />} />
            <Route path="signup" element={<Navigate to="/login" replace />} />
            <Route path="games" element={<Navigate to="/advertisers" replace />} />
            <Route path="case-studies" element={<Navigate to="/partners" replace />} />
            <Route path="active-campaigns" element={<Navigate to="/campaigns" replace />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthInit>
      </I18nProvider>
    </Provider>
  </React.StrictMode>,
);
